import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Results } from '../../types/results';

interface Props {
  results: Results;
}

type FilterType = 'all' | 'correct' | 'incorrect';

const DetailsTab: React.FC<Props> = ({ results }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterType>('all');
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null);

  const filteredAnswers = results.answers.filter(answer => {
    if (filter === 'correct') return answer.is_correct;
    if (filter === 'incorrect') return !answer.is_correct;
    return true;
  });

  const correctCount = results.answers.filter(a => a.is_correct).length;
  const incorrectCount = results.answers.length - correctCount;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-bold text-gray-800">{t('results.answerDetails')}</h3>
        <p className="text-sm text-gray-500">{t('results.reviewAnswers')}</p>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-2 flex-wrap">
        <FilterButton
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          label={`${t('results.allAnswers')} (${results.answers.length})`}
        />
        <FilterButton
          active={filter === 'correct'}
          onClick={() => setFilter('correct')}
          label={`${t('results.correct')} (${correctCount})`}
          activeColor="bg-green-500"
        />
        <FilterButton
          active={filter === 'incorrect'}
          onClick={() => setFilter('incorrect')}
          label={`${t('results.incorrect')} (${incorrectCount})`}
          activeColor="bg-red-500"
        />
      </div>

      {/* Answers List */}
      <div className="space-y-2">
        {filteredAnswers.map((answer, i) => {
          const isExpanded = expandedQuestion === answer.question_number;
          
          return (
            <div
              key={i}
              className={`rounded-lg border-l-4 overflow-hidden transition-all ${
                answer.is_correct
                  ? 'border-green-500 bg-green-50'
                  : 'border-red-500 bg-red-50'
              }`}
            >
              {/* Header - always visible */}
              <button
                onClick={() => setExpandedQuestion(isExpanded ? null : answer.question_number)}
                className="w-full p-3 flex justify-between items-center text-left hover:bg-opacity-80 transition"
              >
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                    answer.is_correct ? 'bg-green-500' : 'bg-red-500'
                  }`}>
                    {answer.question_number}
                  </span>
                  <span className="text-sm text-gray-600 capitalize">
                    {t(`results.section.${answer.question_type.toLowerCase()}`) || answer.question_type}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">{answer.time_spent_seconds}s</span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-0">
                  <div className="bg-white rounded-lg p-3 space-y-2 text-sm">
                    <p className="text-gray-700 font-medium">{answer.question_text}</p>
                    <div className="flex flex-col gap-1 text-sm">
                      <span>
                        {t('results.responseLabel')}:{' '}
                        <strong className={answer.is_correct ? 'text-green-600' : 'text-red-600'}>
                          {answer.selected_answer || t('results.noAnswer') || 'Not answered'}
                        </strong>
                      </span>
                      {!answer.is_correct && (
                        <span>
                          {t('results.correctAnswer') || 'Correct answer'}:{' '}
                          <strong className="text-green-600">{answer.correct_answer.toUpperCase()}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredAnswers.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          {t('results.noAnswersToDisplay') || 'No answers to display with this filter'}
        </div>
      )}
    </div>
  );
};

interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
  activeColor?: string;
}

const FilterButton: React.FC<FilterButtonProps> = ({
  active,
  onClick,
  label,
  activeColor = 'bg-primary'
}) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
      active
        ? `${activeColor} text-white`
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    {label}
  </button>
);

export default DetailsTab;
